import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import { AppColors } from '../utils/utils';

// ─── Data ────────────────────────────────────────────────────────────────────
const pathData = [
  {
    badge: 'Path 1',
    imagePath: '/static/web_page/img_path_1.png',
    title: 'PATH 1 — THE LOT',
    subtitle: '(High-Intent Buyers)',
    howItWorks:
      'When shoppers scan a QR code from your vehicle, they are presented with a professional, informative Auto Spotlight',
  },
  {
    badge: 'Path 2',
    imagePath: '/static/web_page/img_path_2.png',
    title: 'PATH 2 — THE WEB',
    subtitle: '',
    howItWorks:
      'When customers visit your website, they will have immediate access to the QR Buddy interactive shopping experience.',
  },
];

interface PathsSectionProps {
  onOpenImage?: (src: string) => void;
  onPlayVideo?: (url: string) => void;
}

interface PathCardProps {
  badge: string;
  imagePath: string;
  title: string;
  subtitle: string;
  howItWorks: string;
  onOpenImage?: (src: string) => void;
}

interface VideoCardProps {
  onPlayVideo?: (url: string) => void;
}

// ─── Path Card ───────────────────────────────────────────────────────────────
function PathCard({
  badge,
  imagePath,
  title,
  subtitle,
  howItWorks,
  onOpenImage,
}: PathCardProps) {
  return (
    // Top margin gives room for the badge pill that overflows upward by ~50%
    <Box sx={{ mt: { xs: 3, sm: 4 }, height: '100%' }}>
      <Box
        sx={{
          bgcolor: '#d9d9d9',
          borderRadius: '20px',
          px: { xs: 1.5, sm: 2 },
          pb: { xs: 2, sm: 2.5 },
          // pt must be at least badge-pill-height / 2 so content isn't hidden under it
          pt: { xs: '48px', sm: '54px' },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* ── Badge pill ────────────────────────────────────────────── */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            px: { xs: 4, sm: 5 },
            py: { xs: '7px', sm: '9px' },
            borderRadius: '999px',
            border: '2.5px solid #ffe817',
            bgcolor: AppColors.vividBlueColor2,
            color: 'white',
            fontWeight: 700,
            fontSize: { xs: '1.1rem', sm: '1.2rem', lg: '1.35rem' },
            lineHeight: 1,
            minWidth: { xs: 130, sm: 145, lg: 165 },
            textAlign: 'center',
            whiteSpace: 'nowrap',
            zIndex: 1,
            // Yellow sparkle star — floats above-right of the pill
            '&::after': {
              content: '"✦"',
              position: 'absolute',
              top: { xs: '-30px', sm: '-24px' },
              right: { xs: '-8px', sm: '-10px' },
              color: '#ffe817',
              fontSize: { xs: '2rem', sm: '3rem' },
              lineHeight: 1,
              pointerEvents: 'none',
            },
          }}
        >
          {badge}
        </Box>

        {/* ── Feature image ─────────────────────────────────────────── */}
        <Box
          component='img'
          src={imagePath}
          alt={title}
          sx={{
            display: 'block',
            width: '95%',
            maxHeight: '580px',
            mx: 'auto',
            borderRadius: '8px',
            // boxShadow: '0 6px 20px rgba(0,0,0,0.18)',
            // bgcolor: '#fff',
            cursor: 'pointer',
            objectFit: 'contain',
          }}
          onClick={(e) =>
            onOpenImage?.((e.currentTarget as HTMLImageElement).src)
          }
        />

        {/* ── Text block ────────────────────────────────────────────── */}
        <Box sx={{ mt: 2, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1rem', sm: '1.05rem', lg: '1.1rem' },
              lineHeight: 1.2,
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              sx={{
                fontSize: { xs: '1rem', sm: '1.05rem', lg: '1.1rem' },
                mt: 0.5,
                color: 'text.secondary',
              }}
            >
              {subtitle}
            </Typography>
          )}

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1rem', sm: '1.05rem', lg: '1.1rem' },
              mt: 1.5,
              lineHeight: 1.35,
            }}
          >
            How It Works
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '1rem', sm: '1.05rem', lg: '1.1rem' },
              lineHeight: 1.45,
              color: 'text.secondary',
              mt: 0.5,
            }}
          >
            {howItWorks}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Video Card ───────────────────────────────────────────────────────────────
function VideoCard({ onPlayVideo }: VideoCardProps) {
  return (
    // Same top margin so it aligns with path cards in the same row
    <Box sx={{ mt: { xs: 1, sm: 4 }, height: 'auto' }}>
      {/* Blue gradient border wrapper */}
      <Box
        sx={{
          borderRadius: '20px',
          p: { xs: 1.25, sm: 1.75 },
          background:
            'linear-gradient(135deg, #7dd3fc 0%, #60a5fa 55%, #3b82f6 100%)',
          display: 'flex',
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        {/* Inner grey card */}
        <Box
          sx={{
            bgcolor: '#e5e7eb',
            borderRadius: '14px',
            p: { xs: 2, sm: 2.5 },
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          {/* Robot illustration */}
          <Box
            component='img'
            src='/static/web_page/img_web_robots_fighting .png'
            alt='Miles vs Bots'
            sx={{
              maxWidth: { xs: 200, sm: 250, lg: 290 },
              width: '65%',
              alignSelf: 'center',
              mb: 1,
            }}
          />

          {/* Play Button */}
          <Button
            variant='outlined'
            startIcon={
              <PlayCircleIcon
                fontSize='inherit'
                sx={{
                  color: 'red',
                }}
              />
            }
            sx={{
              bgcolor: '#fff',
              color: 'black',
              borderRadius: 10,
              px: 0,
              py: 0,
              pr: 2,
              mb: 3,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              fontWeight: 600,
              textTransform: 'none',
              alignSelf: 'center',
              border: '2px solid red',
              '&:hover': {
                // bgcolor: '#DC2626',
              },
              '& .MuiButton-startIcon svg': {
                fontSize: 36,
              },
            }}
            onClick={() =>
              onPlayVideo?.(
                'https://assets.qrbuddy.ai/web/Spot-AI-over-Chatbot.mp4',
              )
            }
          >
            Play Video
          </Button>

          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              fontSize: { xs: '1.05rem', sm: '1.15rem' },
              lineHeight: 1.25,
            }}
          >
            Miles vs Bots
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              fontSize: { xs: '0.95rem', sm: '1.05rem' },
              lineHeight: 1.25,
              mb: 1.5,
            }}
          >
            How to Spot Real AI from Fake Chatbots
          </Typography>

          <Typography
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
              mb: 0.25,
            }}
          >
            Description
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
              lineHeight: 1.5,
              mb: 1,
            }}
          >
            Not all &ldquo;AI&rdquo; is created equal. Most dealership chat
            tools are nothing more than scripted bots that can&apos;t think,
            adapt, or truly understand your buyers.
          </Typography>
          <Typography
            sx={{
              color: 'text.secondary',
              fontSize: { xs: '0.9rem', sm: '0.95rem' },
              lineHeight: 1.5,
            }}
          >
            Miles is a real AI Agent built on large-language intelligence, real
            conversation flow, and live decision-making so your customers get
            real answers, real engagement, and real results.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main Section ─────────────────────────────────────────────────────────────
export default function PathsSection({
  onOpenImage,
  onPlayVideo,
}: PathsSectionProps) {
  return (
    <Box sx={{ py: 2 }}>
      <Box
        sx={{
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: '6%', md: '8%', lg: '10%' },
          background: AppColors.aiGradient8,
        }}
      >
        {/*
         * Responsive column layout via MUI Grid:
         *
         *   xs  (< 600px)    xs=12 + xs=12 + xs=12  → all 3 stacked
         *   sm  (600–1200px) sm=6  + sm=6  + sm=12  → 2 path cards side-by-side,
         *                                              video card full-width below
         *   lg  (≥ 1200px)   lg=4  + lg=4  + lg=4   → 3 equal columns
         */}
        <Grid
          container
          spacing={{ xs: 2, sm: 2.5 }}
          alignItems='center'
          alignContent={'center'}
        >
          {/* ── Path 1 ── */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <PathCard {...pathData[0]} onOpenImage={onOpenImage} />
          </Grid>

          {/* ── Path 2 ── */}
          <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
            <PathCard {...pathData[1]} onOpenImage={onOpenImage} />
          </Grid>

          {/* ── Miles vs Bots video card ── */}
          <Grid size={{ xs: 12, sm: 12, lg: 4 }}>
            <VideoCard onPlayVideo={onPlayVideo} />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
